<template>
	<BaseModal :show="show" border-classes="border border-neutral-subtle">
		<form class="flex flex-col h-fit w-100 bg-neutral">
			<div class="flex">
				<p class="flex-1 text-xl font-bold leading-7 text-heading">Invite users</p>
				<img src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="cursor-pointer" @click="dismiss">
			</div>

			<p class="text-sm font-medium text-label mt-1">Enter one or more email addresses</p>

			<div>
				<vue-tags-input
					id="tag-input"
					placeholder="email@example.com"
					v-model="tag"
					:tags="tags"
					:validation="validation"
					:delete-on-backspace="false"
					:add-on-key="[13, ' ', ',', ';']"
					@tags-changed="newTags => tags = newTags"
					class="userpilot-mask mt-3"
				/>
			</div>
			<ValidationError :errors="error" field="email_addresses" class="mt-2 ml-3.5" />

			<div class="flex flex-col mt-3">
				<EndpointCombobox v-if="authStore.isPortalAdmin" id="organizations" v-model="selectedOrganization" title="Select an organization to add the user(s) to:" placeholder="Enter organization name"
									label-text-size="text-sm" label-class="font-medium text-label !mb-2" input-height="h-10" icon="edit" :errors="error" error-field="organization_id"
									:enterable="true" :show-options-on-top="true" :endpoint="`organizations/organizations/search-by-name?tenantId=${tenant.id}&name=`"/>

				<Combobox v-else id="organizations" v-model="selectedOrganizationName" :items="organizationNames" title="Select an organization to add the user(s) to:" placeholder="Enter organization name"
									label-text-size="text-sm" label-class="font-medium text-label !mb-2" input-height="h-10" icon="edit" :errors="error" error-field="organization_id"
									:enterable="false" :readonly="true" :show-options-on-top="true"/>
			</div>

			<SubmitButton label="Send Invites" :disabled="!sendEnabled || isSendingInvites" :loading="isSendingInvites" @click.stop="onSendInvite" class="mt-8" />
		</form>
	</BaseModal>
</template>

<script setup lang="js">
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { computed, ref, watch } from 'vue'
import { VueTagsInput } from '@vojtechlanka/vue-tags-input'
import { Toaster, Banner } from '@/models/utils/Toaster.js';
import { userAdminApi } from '@/api/User/UserAdminApi.js'
import { useAuthStore } from '@/stores/authStore.js'
import Combobox from '@/components/Forms/Combobox.vue'
import ValidationError from '@/components/Forms/InputError.vue'
import EndpointCombobox from '@/components/Forms/EndpointCombobox.vue'
import { Tenants } from '@/models/tenants/Tenants.js'
import BaseModal from '@/components/Alerts/BaseModal.vue'

const props = defineProps({
	show: { type: Boolean, required: false }
})

const emit = defineEmits(['onDismiss'])

const isSendingInvites = ref(false)
const tag = ref('')
const tags = ref([])
const organizations = ref([])
const selectedOrganization = ref(null)
const organizationNames = ref([])
const selectedOrganizationName = ref(null)
const error = ref(null)
const tenant = Tenants.getTenant()

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const validation = [{
	classes: 'email-only',
	rule: emailRegex,
	disableAdd: true
}]
const authStore = useAuthStore()

const toaster = new Toaster();
const banner = new Banner();

const sendEnabled = computed(() => {
	return selectedOrganization.value != null && getEmails().length !== 0;
})

watch(
	() => props.show,
	(newValue) =>
	{
		if (newValue)
		{
			//Load data when shown
			loadData(newValue)
		}
	}
)

function loadData() {
	organizations.value = authStore.userOrganizations.filter((organization) => { return authStore.isPortalAdmin || organization.admin_permissions.admin.enabled })
	organizationNames.value = organizations.value.map((organization) => {
		return organization.organization_name
	})
	if (!authStore.isPortalAdmin) {
		selectedOrganization.value = organizations.value[0]
		selectedOrganizationName.value = organizationNames.value[0]
	}
}

/*
Replaced with the above. If a user wasn't a portal admin, the org field would be cleared out after sending and it wouldn't populate again without reloading the page.
onMounted(() => {
	organizations.value = authStore.userOrganizations.filter((organization) => { return authStore.isPortalAdmin || organization.admin_permissions.admin })
	organizationNames.value = organizations.value.map((organization) => {
		return organization.organization_name
	})
	if (!authStore.isPortalAdmin) {
		selectedOrganization.value = organizations.value[0]
		selectedOrganizationName.value = organizationNames.value[0]
	}
})*/


function onSendInvite()
{
	isSendingInvites.value = true

	sendInvite(getEmails())
}

function getEmails() {
	//This was throwing errors on load
	try
	{
		// It is possible the user clicks send before the email has been added to the tags list, so try to process it here
		const tagInput = document.getElementById('tag-input')
		const inputText = emailRegex.test(tagInput.value) ? tagInput.value : null

		const tagTexts = tags.value.map(tag => tag.text)

		if (inputText)
		{
			tagTexts.push(inputText)
		}

		return tagTexts
	}
	catch (e) { /* empty */ }

	return tags.value.map(tag => tag.text)
}

/**
 * Send user invites
 * @param tagTexts {Array<String>} Array of email addresses
 * @author Andrew Latus (2025-10-10)
 */
function sendInvite(tagTexts)
{
	if (!authStore.isPortalAdmin) {
		selectedOrganization.value = organizations.value.find((organization) => { return organization.organization_name === selectedOrganizationName.value })
	}

	const orgId = selectedOrganization.value?.id

	if (!orgId)
	{
		toaster.error("Please select an organization.");
	}
	else
	{
		error.value = null
		/*Example response
		{
			"status": "success",
			"org_created": [],
			"existing_user_orgs": [
				"jclark@uhin.org"
			],
			"error_creating_user_org": [],
			"error_inviting": [],
			"errors": []
		}*/
		userAdminApi.sendInvites({
				'email_addresses': tagTexts,
				'invitee_user_id': authStore.getAuth?.user()?.id,
				'organization_id': orgId,
				'organization_role_id': 0 //Used by batch import, 0 assigns user to role marked as default in organization_roles
			},
			(p_data) =>
			{
				isSendingInvites.value = false
				let banner_shown = false;

				let warn_msg = "", info_msg = "";
				if (p_data["existing_user_orgs"].length > 0)
				{
					warn_msg += (p_data["existing_user_orgs"].length > 1) ? "These users have " : "This user has ";
					warn_msg += "an existing account with the selected organization: " + p_data["existing_user_orgs"].map(p_item => p_item.text).join(", ") + "<br>";
				}
				if (p_data["org_created"].length > 0)
				{
					info_msg += (p_data["org_created"].length > 1) ? "These users have " : "This user has ";
					info_msg += "an existing account, the organization has been added to their account: " + p_data["org_created"].map(p_item => p_item.text).join(", ") + "<br>";
				}

				// User already has an account with org
				if (warn_msg.length > 0)
				{
					banner.warning(warn_msg);
					banner_shown = true
				}

				// Org added to user's account
				if (info_msg.length > 0)
				{
					banner.info(info_msg);
					banner_shown = true
				}

				if (p_data["errors"].length > 0)
				{
					let err_msg = "";
					if (p_data["error_inviting"].length > 0)
					{
						const msg = (p_data["error_inviting"].length > 1) ? "these users: " : "this user: ";
						err_msg += "There was an error inviting " + msg + p_data["error_inviting"].map(p_item => p_item.text).join(", ") + "<br><br>";
					}

					err_msg += "Errors: <br/>" + p_data["errors"].join("<br>");
					banner.error(err_msg);

					banner_shown = true
				}

				if (!banner_shown)//Show basic toast if everything went well
				{
					toaster.success('Invites sent successfully!')
				}
				else if (p_data["sent_invites"].length > 0)
				{
					let success_msg = (p_data["sent_invites"].length > 1) ? "Invites sent to: " : "Invite sent to: ";
					success_msg += p_data["sent_invites"].map(p_item => p_item.text).join(", ") + "<br>";
					banner.success(success_msg)
				}

				dismiss()
			},
			(e) =>
			{
				error.value = e.showErrorBanner()
				isSendingInvites.value = false
			})
	}

}

function dismiss() {
	isSendingInvites.value = false
	tag.value = ''
	tags.value = []
	organizations.value = []
	selectedOrganization.value = null
	organizationNames.value = []
	selectedOrganizationName.value = null
	error.value = null
	tenant.value = null

	emit('onDismiss')
}

</script>

<style scoped>

/* Tag being entered */
.vue-tags-input .ti-new-tag-input {
	background: transparent;
	color: #697296;
	font-weight: 500;
	border: 0 none;
	box-shadow: none;
}

/* Outer box, default */
.vue-tags-input .ti-input {
	padding: 4px 12px;
	border: 1px solid #B2BBC9;
	border-radius: 8px;
	transition: border-bottom 200ms ease;
}

/* Outer box, focus */
.vue-tags-input.ti-focus .ti-input {
	border: 1px solid; /*#0B84FF*/
	@apply border-neutral-subtle bg-neutral;
	border-radius: 8px;
}

/* Placeholder text color, supporting all browsers */
.vue-tags-input ::-webkit-input-placeholder {
	@apply placeholder-default;
}

.vue-tags-input ::-moz-placeholder {
	@apply placeholder-default;
}

.vue-tags-input :-ms-input-placeholder {
	@apply placeholder-default;
}

.vue-tags-input :-moz-placeholder {
	@apply placeholder-default;
}

/* All tags, default */
.vue-tags-input .ti-tag {
	position: relative;
	background: transparent;
	/*border: 1px solid;#E66F0E;*/
	@apply border-neutral bg-neutral;
	border-radius: 4px;
	@apply text-heading;
}

/* Invalid tag */
.vue-tags-input .ti-tag.ti-valid {
	@apply bg-accent;
}

/* Invalid tag */
.vue-tags-input .ti-tag.ti-invalid {

	@apply bg-disabled;
}

/* Invalid input */
.vue-tags-input .ti-new-tag-input.ti-invalid {
	/*color: #e88a74;*/
}

/* Duplicate tag, crossed out */
.vue-tags-input .ti-duplicate span,
.vue-tags-input .ti-new-tag-input.ti-duplicate {
	text-decoration: line-through;
}
</style>